import { Icon } from "@templates/design-system";
import { getComponent } from "../../../../client";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import type { IFooterBlockProps } from "./FooterBlock.props";

export const FooterBlock = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  parentClass,
  secondaryLinks,
  socialLinks,
}: IFooterBlockProps) => {
  if (
    !logo &&
    !description &&
    !contact?.email &&
    !contact?.phone &&
    !contact?.address &&
    !navigationGroups?.length &&
    !socialLinks?.length &&
    !secondaryLinks?.length &&
    !copyright
  )
    return null;
  const Link = getComponent("Link");
  const linkClass =
    "inline-flex min-h-11 items-center font-daylight-body text-sm text-slate transition-colors hover:text-cobalt focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
  return (
    <footer className={`${parentClass || ""} bg-sky text-ink`}>
      <div className="daylight-container py-12 md:py-16">
        <div className="grid gap-8 rounded-[28px] border border-cobalt/20 bg-daylight/70 p-7 md:gap-12 lg:grid-cols-[1.25fr_2fr_0.8fr] lg:p-10">
          <div>
            {logo ? (
              <DaylightMedia
                className="mb-6 h-11 w-48 bg-transparent"
                imageClassName="object-contain object-left"
                media={logo}
                sizes="192px"
              />
            ) : null}
            {description ? (
              <DaylightRichText
                className="max-w-sm text-base text-slate md:text-[17px]"
                content={description}
              />
            ) : null}
            {contact ? (
              <address className="mt-5 flex flex-col not-italic">
                {contact.email ? (
                  <a className={linkClass} href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                ) : null}
                {contact.phone ? (
                  <a className={linkClass} href={`tel:${contact.phone}`}>
                    {contact.phone}
                  </a>
                ) : null}
                {contact.address ? (
                  <p className="mt-2 font-daylight-body text-sm leading-6 text-slate">
                    {contact.address}
                  </p>
                ) : null}
              </address>
            ) : null}
          </div>
          {navigationGroups?.length ? (
            <nav aria-label="Footer" className="grid grid-cols-2 gap-8 lg:grid-cols-3">
              {navigationGroups.map((group, index) => (
                <div key={index}>
                  <h2 className="mb-3 font-daylight-display text-sm font-bold">{group.header}</h2>
                  <ul>
                    {group.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          aria-label={link.accessibilityLabel || undefined}
                          className={linkClass}
                          href={link.href}
                          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                          target={link.openInNewTab ? "_blank" : undefined}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          ) : null}
          {socialLinks?.length ? (
            <div className="flex flex-wrap content-start gap-2">
              {socialLinks.map((item, index) => (
                <Link
                  aria-label={item.link.accessibilityLabel || undefined}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-cobalt/25 px-4 font-daylight-body text-sm text-ink hover:bg-cobalt hover:text-daylight focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={item.link.href}
                  key={index}
                  rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={item.link.openInNewTab ? "_blank" : undefined}
                >
                  {item.icon ? <Icon className="size-4" name={item.icon} /> : null}
                  {item.link.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-6 flex flex-col gap-3 border-t border-cobalt/20 pt-5 font-daylight-body text-sm text-slate sm:mt-8 sm:flex-row sm:items-center sm:justify-between sm:pt-6">
          {secondaryLinks?.length ? (
            <div className="flex flex-wrap gap-x-5">
              {secondaryLinks.map((link, index) => (
                <Link
                  aria-label={link.accessibilityLabel || undefined}
                  className={linkClass}
                  href={link.href}
                  key={index}
                  rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={link.openInNewTab ? "_blank" : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ) : null}
          {copyright ? <p>{copyright}</p> : null}
        </div>
      </div>
    </footer>
  );
};

FooterBlock.displayName = "Block::FooterBlock";
